import * as React from 'react';

import { EntityDetails, Select } from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';

import { $remoteFacilities } from '../../_state/facilities.state';
import * as state from '../../_state/inventoryMovementHistory.state';
import { useTranslations } from '../../i18n/useTranslations';
import {
  AllocateToLotDialog,
  type MovementForAllocationDialog,
} from '../lots/allocation/AllocateToLotDialog';

import { FacilityBreakdownTab } from './FacilityBreakdownTab';
import { MovementHistoryTableContent } from './MovementHistoryTableContent';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

type MovementHistoryTab = 'facilities' | 'history';

export const InventoryMovementHistoryView = () => {
  const t = useTranslations();
  const [, setLocation] = useModuleLocation();

  const [currentTab, setCurrentTab] =
    React.useState<MovementHistoryTab>('facilities');

  const itemName = useAtomValue(state.$itemName) || 'Item';
  const levelsListFacilityFilter = useAtomValue(
    state.$levelsListFacilityFilter,
  );
  const { data: facilitiesData } = useAtomValue($remoteFacilities);
  const { data, isFetching, loadingState, error } = useAtomValue(
    state.$remoteInventoryMovementHistory,
  );

  const [facilityId, setFacilityId] = useAtom(state.$facilityId);
  const [allocateDialogOpen, setAllocateDialogOpen] = useAtom(
    state.$allocateDialogOpen,
  );
  const [selectedMovement, setSelectedMovement] = useAtom(
    state.$selectedMovementForAllocation,
  );

  const movements = data?.movements ?? [];

  const facilities = React.useMemo(
    () => facilitiesData?.facilities ?? [],
    [facilitiesData?.facilities],
  );

  const facilityOptions = React.useMemo(
    () => [
      {
        key: 'all',
        label: t.movementHistoryView.filters.allFacilities,
      },
      ...facilities.map((f) => ({ key: f.id, label: f.name })),
    ],
    [facilities, t],
  );

  const handleBack = React.useCallback(() => {
    setLocation('/inventory/levels', {
      query:
        levelsListFacilityFilter && levelsListFacilityFilter !== 'all'
          ? { f: levelsListFacilityFilter }
          : undefined,
    });
  }, [levelsListFacilityFilter, setLocation]);

  const handleCloseAllocateDialog = React.useCallback(() => {
    setAllocateDialogOpen(false);
    setSelectedMovement(null);
  }, [setAllocateDialogOpen, setSelectedMovement]);

  const handleFacilityChange = React.useCallback(
    (key: string) => {
      setFacilityId(key === 'all' ? undefined : key);
    },
    [setFacilityId],
  );

  React.useEffect(() => {
    if (facilityId && facilityId !== 'all') {
      setCurrentTab('history');
    } else {
      setCurrentTab('facilities');
    }
  }, [facilityId]);

  const selectedMovementForDialog = React.useMemo(():
    | MovementForAllocationDialog[]
    | null => {
    if (!selectedMovement || !movements.length) return null;

    const movement = movements.find(
      (m) => m.id === selectedMovement.movementId,
    );
    if (!movement) return null;

    return [
      {
        id: movement.id,
        itemName: movement.itemName,
        occurredAt: movement.occurredAt,
        quantity: movement.quantity,
        unallocatedQuantity: movement.quantity,
        quantityUnit: movement.quantityUnit,
        originTransactionType: movement.originTransactionType,
        originTransactionItemId: movement.originTransactionItemId,
      },
    ];
  }, [selectedMovement, movements]);

  return (
    <>
      <EntityDetails
        className="@InventoryMovementHistoryView"
        error={error}
        loading={loadingState.isInitialLoading || isFetching}
        onBackPressed={handleBack}
        primaryTitle={itemName}
        subtitle={t.movementHistoryView.inventoryDetails}
        headerActions={
          <Select
            value={facilityId || 'all'}
            onChange={handleFacilityChange}
            options={facilityOptions}
            label={t.movementHistoryView.facilitySelector}
            className="w-52"
          />
        }
        tabs={{
          facilities: {
            title: t.movementHistoryView.tabs.facilities,
            content: FacilityBreakdownTab,
          },
          history: {
            title: t.movementHistoryView.tabs.history,
            content: MovementHistoryTableContent,
          },
        }}
        currentTab={currentTab}
        onChangeTab={(tab) => setCurrentTab(tab as MovementHistoryTab)}
      />

      {selectedMovementForDialog && selectedMovement && (
        <AllocateToLotDialog
          isOpen={allocateDialogOpen}
          onClose={handleCloseAllocateDialog}
          movements={selectedMovementForDialog}
          facilityId={selectedMovement.facilityId}
        />
      )}
    </>
  );
};
